'use client';

import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/toaster';
import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SubscriptionProvider } from '../contexts/SubscriptionContext';
import { AuthProvider } from '../contexts/AuthContext';
import { useAuthContext } from '../hooks/useAuthContext';
import { AppSidebar } from './AppSidebar';
import { Separator } from './ui/separator';
import LoadingSpinner from './LoadingSpinner';
import { CookieConsent } from './CookieConsent';

// Mobile breakpoint - consistent with shadcn/ui
const MOBILE_BREAKPOINT = 768;

const AuthenticatedLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [previousPath, setPreviousPath] = useState(location.pathname);

  // Track mobile state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

  // Initialize sidebar state based on screen width (open on desktop, closed on mobile)
  // But after initialization, this state will track the toggle state independently
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);

  // Flag to track if this is the initial render
  const initialRenderRef = useRef(true);

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);

      // Only auto-adjust sidebar on resize if this is the initial render
      if (initialRenderRef.current) {
        setSidebarOpen(!newIsMobile);
        initialRenderRef.current = false;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle navigation - close sidebar on mobile when path changes
  useEffect(() => {
    // Only close if we've actually navigated to a new path
    if (location.pathname !== previousPath) {
      console.log('Navigation detected:', location.pathname);
      console.log('Previous path:', previousPath);
      console.log('Is mobile:', isMobile);
      console.log('Sidebar open:', sidebarOpen);

      if (isMobile && sidebarOpen) {
        console.log('Closing sidebar due to navigation on mobile');
        setSidebarOpen(false);
      }

      setPreviousPath(location.pathname);
    }
  }, [location.pathname, previousPath, isMobile, sidebarOpen]);

  // Authentication check
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname !== '/signin') {
      navigate('/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated && location.pathname !== '/signin') {
    return null; // This will prevent rendering anything while redirecting
  }

  return (
    <SubscriptionProvider>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={(open) => {
          console.log('Sidebar toggle clicked, new state:', open);
          setSidebarOpen(open);
        }}
      >
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4 sticky top-0 z-10 bg-background">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Link to="/" className="flex items-center gap-2 font-semibold">
              <img src="/icon.svg" alt="Creative AI Writer Icon" className="h-6 w-6" />
              Creative AI Writer
            </Link>
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4">
            <main className="min-h-[calc(100vh-4rem)] flex-1 rounded-xl bg-muted/50 md:min-h-[calc(100vh-4rem)]">
              <Outlet />
            </main>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </SubscriptionProvider>
  );
};

const Layout: React.FC = () => {
  return (
    <AuthProvider>
      <AuthenticatedLayout />
      <Toaster />
      <CookieConsent />
    </AuthProvider>
  );
};

export default Layout;
